import React, { useMemo } from "react";

import { Attribute, MdmDeviceDto, MdmObjectDto } from "gql/generated/types";

type AdditionalInfoProps<T> = {
  attributes: (Attribute | null)[];
  objectState: T;
};

const AdditionalInfo = <T extends MdmObjectDto | Partial<MdmDeviceDto>>({
  attributes,
  objectState,
}: AdditionalInfoProps<T>) => {
  const attributeMap = useMemo(
    () => new Map(objectState.customFields?.map(field => [field?.key, field?.value])),
    [objectState.customFields]
  );

  const attributeGroups = useMemo(
    () =>
      [...attributes].reduce((acc, curr) => {
        const parent = curr?.parent?.toString();
        if (!parent || !curr) return acc;
        const parentAttributes = acc.get(parent);
        if (!parentAttributes) return acc.set(parent, [curr]);
        return acc.set(parent, [...parentAttributes, curr]);
      }, new Map<string, Attribute[]>()),
    [attributes]
  );

  const nodes = useMemo(() => [...attributeGroups].map(([key, value]) => (
    <table key={key} className="info-table">
      <caption>{key}</caption>
      <tbody>
        {value.map((attr: Attribute) => (
          <tr key={attr.id}>
            <th>{attr.title}</th>
            <td>{attributeMap.get(attr.id)?.value ?? "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )), [attributeGroups, attributeMap]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{nodes}</>;
};

export default AdditionalInfo;